import './EvaluationRecap.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { Alert, Card, Col, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { VizClient } from '../../api/VizClient';
import { useToasts } from '../../contexts/toasts';
import { getConnectionDetails, createQueryDetails } from '../../utils';
import AppLoader from '../common/AppLoader';
import { BdaDataset } from '../../models/BdaDataset';


interface Metric {
  label: string;
  description: string;
  type: string;
  value: any; // TODO improve type
}

type Props = {
  dataset: BdaDataset;
}

function EvaluationRecap({ dataset }: Props) {
  const { showToast } = useToasts();
  const [error, setError] = useState(false);
  const [metrics, setMetrics] = useState<Metric[]>();

  useEffect(() => {
    initMetrics();
    // eslint-disable-next-line
  }, []);

  const initMetrics = () => {
    const connectionDetails = getConnectionDetails(dataset.datasource!, undefined, dataset.datasetFileType);
    const query = `SELECT * FROM ${dataset.datasetFileType!.tableName}`;
    const body = createQueryDetails({ ...connectionDetails, query });

    VizClient.getQueryResult(body, dataset.datasource!.driverType)
      .then(
        ({ data: res }) => {
          if (!res || !res['result']) {
            setError(true);
            showToast('metrics-error', "Could not get evaluation metrics.", "Evaluation Error", 'danger');
          }
          else {
            const metrics = res['result']['data'];
            setMetrics(metrics);
          }
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('matrics-error', message, `Metrics Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderMetrics = () => {
    const metricsImages = metrics!.filter(metric => metric.type === 'image').map(img => {
      return {
        title: img.label,
        src: `data:image/jpg;base64,${img.value}`,
      }
    });
    const metricsWidgets = metrics!.filter(metric => metric.type === 'number' || metric.type === 'text').map((metric, index) => ({
      title: metric.label,
      value: Number.parseFloat(metric.value) ? Number.parseFloat(metric.value).toFixed(4) : metric.value,
      type: metric.type,
      description: metric.description,
    }));
    const metricsTables = metrics!
      .filter(metric => metric.type === 'table')
      .map(table => {
        const data = JSON.parse(table.value.replace(/'/g, '"'));
        return { label: table.label, value: data }
      });

    return (
      <Tabs defaultActiveKey="overview">
        <Tab eventKey="overview" title="Overview">
          <div className="p-1">
            <Row className="gx-0 justify-content-center mb-3">
              {metricsWidgets.map((widget, index) => (
                <Col md={6} lg={4} xl={3} key={index} className="p-1">
                  <Card className="widget">
                    <h6 className="widget-title">{widget.title}</h6>
                    <p className="widget-value">{widget.value}</p>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row className="gx-2">
              {metricsImages.map((img, index) => (
                <Col key={index} md={4}>
                  <div className="border p-2 text-center">
                    <img src={img.src} width="100%" alt={`Evaluation chart ${index + 1}: ${img.title}`} />
                    <h6>{img.title}</h6>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Tab>

        {metricsTables.map((table, index) => {
          const tableHeading = Object.keys(table.value[0]);

          return (
            <Tab key={index} eventKey={`metrics-table-${index}`} title={table.label}>
              <div className="p-2">
                <Table bordered>
                  <thead>
                    <tr>
                      {tableHeading.map((col, i) => (
                        <th key={i}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(table.value as any[]).map((row, i) => (
                      <tr key={i}>
                        {tableHeading.map((col, k) => (
                          <td key={k}>{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          )
        })}
      </Tabs>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured. Please, try again later.</span>
      </Alert>
    )

    return metrics
      ? renderMetrics()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={300} />
        </div>
      );
  }

  return renderAsyncContent();
}

export default memo(EvaluationRecap);